// Shared "new message" notification tone - a short, snappy two-note pop in
// the style of WhatsApp's incoming-message sound, synthesized with Web Audio
// (not a copy of WhatsApp's actual proprietary audio file). Used everywhere
// a real-time alert should sound the same: direct messages, GMD approvals,
// procurement reviews.

const FALLBACK_SOUND =
  'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBCuAzvLaizsIGGS57OihUhELTqvn77BdGAg+ltryxnMpBSl+zPLTgjMGHm7A7+OZURE';

let sharedAudioCtx = null;
const getAudioContext = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return null;
    if (!sharedAudioCtx || sharedAudioCtx.state === 'closed') {
      sharedAudioCtx = new AudioCtx();
    }
    if (sharedAudioCtx.state === 'suspended') {
      sharedAudioCtx.resume().catch(() => {});
    }
    return sharedAudioCtx;
  } catch {
    return null;
  }
};

// Unlock on the first user gesture, since browsers block audio until then.
if (typeof window !== 'undefined') {
  const unlock = () => getAudioContext();
  window.addEventListener('pointerdown', unlock, { once: true, passive: true });
  window.addEventListener('touchstart', unlock, { once: true, passive: true });
  window.addEventListener('keydown', unlock, { once: true });
}

const playFallbackSound = () => {
  try {
    const audio = new Audio(FALLBACK_SOUND);
    audio.volume = 0.6;
    audio.play().catch(() => {});
  } catch {
    // Sound must never break the app.
  }
};

let lastPlayedAt = 0;

/** Plays the shared WhatsApp-style "pop" notification tone. Never throws. */
export const playNotificationSound = async () => {
  const now = Date.now();
  if (now - lastPlayedAt < 1200) return; // avoid overlapping pops on a burst of events
  lastPlayedAt = now;

  try {
    const ctx = getAudioContext();
    if (!ctx) return playFallbackSound();
    if (ctx.state === 'suspended') {
      try {
        await ctx.resume();
      } catch {
        return playFallbackSound();
      }
    }
    if (ctx.state !== 'running') return playFallbackSound();

    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate([120, 60, 120]);
    }

    // Two-note "pop-pop", high note then a lower note, ~260ms total -
    // WhatsApp-style: bright, percussive, quick attack/decay.
    const notes = [
      { freq: 1567.98, start: 0, dur: 0.11 }, // G6
      { freq: 1174.66, start: 0.1, dur: 0.16 }, // D6
    ];

    notes.forEach((n) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = n.freq;
      const s = ctx.currentTime + n.start;
      gain.gain.setValueAtTime(0.0001, s);
      gain.gain.exponentialRampToValueAtTime(0.85, s + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, s + n.dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(s);
      osc.stop(s + n.dur + 0.03);
    });
  } catch {
    playFallbackSound();
  }
};

export default playNotificationSound;
